import { useState, useEffect, RefObject, forwardRef } from 'react'
import { Button, Table, Text, Accordion, CardColors, Card } from '@/ui'
import { TypographyStyles } from '@/styles'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { centsToDollars } from '@/utils/currencyHelpers'
import { JournalEntryType, GeneralLedgerLineItem } from '../types'
import {
  GeneralLedgerAccount,
  NUM_ITEMS_IN_COLLAPSED_STATE,
} from './GeneralLedger'
import { formatFinancialStatement } from '../service'
import { isoToUTCDateTime, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { TransactionAccountTypes } from '@/features/reports/reports.slice'

interface Props {
  account: GeneralLedgerAccount
  active: boolean
  index: number
  onRowClick: (index: number) => void
  forwardedRef: RefObject<HTMLDivElement> | null
}
export const GeneralLedgerRow = forwardRef(
  ({ account, index, onRowClick, forwardedRef }: Props, _ref) => {
    const [isLineItemExpanded, setIsLineItemExpanded] = useState(false)
    const [visibleLineItems, setVisibleLineItems] = useState<
      GeneralLedgerLineItem[]
    >([])

    useEffect(() => {
      if (!isLineItemExpanded) {
        setVisibleLineItems(
          account.lineItems.slice(0, NUM_ITEMS_IN_COLLAPSED_STATE)
        )
      } else {
        setVisibleLineItems(account.lineItems)
      }
    }, [isLineItemExpanded, account.lineItems])

    const toggleLineItemListState = () =>
      setIsLineItemExpanded((state) => !state)

    return (
      <div key={account.key} ref={forwardedRef} className="mb-6">
        <Card className={forwardedRef ? CardColors.PRIORITY : undefined}>
          <Accordion type="single" collapsible>
            <Accordion.Item
              value={`account-${account.id}`}
              className="border-none"
            >
              <Accordion.Trigger
                onClick={() => onRowClick(index ?? 0)}
                className="w-full flex items-center justify-between px-4 py-3"
              >
                <div className="grid grid-cols-12 w-full items-center gap-4">
                  <div className="col-span-5">
                    <Text variant={TypographyStyles.BODY_LG}>
                      {account.name}
                    </Text>
                  </div>
                  <div className="col-span-3">
                    <Text variant={TypographyStyles.BODY_MD}>
                      {account.accountType}
                    </Text>
                  </div>
                  <div className="col-span-2">
                    <Text variant={TypographyStyles.BODY_MD}>
                      {formatFinancialStatement(account.financialStatement)}
                    </Text>
                  </div>
                  <div className="col-span-2 text-right">
                    <Text variant={TypographyStyles.BODY_MD}>
                      <CurrencyFormatLabel
                        value={centsToDollars(account.netTotalInCents)}
                      />
                    </Text>
                  </div>
                </div>
              </Accordion.Trigger>

              <Accordion.Content>
                <div className="flex flex-col gap-4 px-4 pb-4">
                  {/* Top summary */}
                  <div className="flex justify-between items-center">
                    <Text
                      variant={TypographyStyles.BODY_SM}
                      className="text-blue-200"
                    >
                      Showing {visibleLineItems.length} of{' '}
                      {account.lineItems.length}
                    </Text>
                    {account.lineItems.length >
                      NUM_ITEMS_IN_COLLAPSED_STATE && (
                      <Button variant="link" onClick={toggleLineItemListState}>
                        {isLineItemExpanded
                          ? 'View less journal entries'
                          : 'View all journal entries'}
                      </Button>
                    )}
                  </div>

                  {/* Table */}
                  <Table>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Date</Table.HeaderCell>
                        <Table.HeaderCell>ID</Table.HeaderCell>
                        <Table.HeaderCell>Description</Table.HeaderCell>
                        <Table.HeaderCell>Debit</Table.HeaderCell>
                        <Table.HeaderCell>Credit</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {visibleLineItems.map((item) => (
                        <Table.Row key={item.id}>
                          <Table.Cell>
                            {item.journalEntryDate &&
                              isoToUTCDateTime(item.journalEntryDate).toFormat(
                                DATE_FORMATS_LUXON.DISPLAY_SHORT
                              )}
                          </Table.Cell>
                          <Table.Cell>{item.id}</Table.Cell>
                          <Table.Cell>
                            {item.transactionDescription ||
                              item.journalEntryDetails}
                          </Table.Cell>
                          <Table.Cell>
                            {item.entryType === JournalEntryType.DEBIT && (
                              <CurrencyFormatLabel
                                value={centsToDollars(item.amountInCents)}
                              />
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            {item.entryType === JournalEntryType.CREDIT && (
                              <CurrencyFormatLabel
                                value={centsToDollars(item.amountInCents)}
                              />
                            )}
                          </Table.Cell>
                        </Table.Row>
                      ))}

                      {/* Balance brought down row */}
                      {!isLineItemExpanded &&
                        account.lineItems.length >
                          NUM_ITEMS_IN_COLLAPSED_STATE && (
                          <Table.Row>
                            <Table.Cell />
                            <Table.Cell />
                            <Table.Cell>
                              Sum of hidden transactions{' '}
                              <Button
                                variant="link"
                                onClick={toggleLineItemListState}
                              >
                                (View all)
                              </Button>
                            </Table.Cell>
                            <Table.Cell>
                              <CurrencyFormatLabel
                                value={centsToDollars(
                                  account.balanceBroughtDownDetails.sumDebits
                                )}
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <CurrencyFormatLabel
                                value={centsToDollars(
                                  account.balanceBroughtDownDetails.sumCredits
                                )}
                              />
                            </Table.Cell>
                          </Table.Row>
                        )}

                      {/* Total debits and credits */}
                      <Table.Row>
                        <Table.Cell />
                        <Table.Cell />
                        <Table.Cell className="border-b border-muted">
                          <Text variant={TypographyStyles.BODY_SM}>
                            <b>Total {account.name}</b>
                          </Text>
                        </Table.Cell>
                        <Table.Cell className="border-b border-muted">
                          <Text variant={TypographyStyles.BODY_SM}>
                            <b>
                              <CurrencyFormatLabel
                                value={centsToDollars(
                                  account.totalDebitsInCents
                                )}
                              />
                            </b>
                          </Text>
                        </Table.Cell>
                        <Table.Cell className="border-b border-muted">
                          <Text variant={TypographyStyles.BODY_SM}>
                            <b>
                              <CurrencyFormatLabel
                                value={centsToDollars(
                                  account.totalCreditsInCents
                                )}
                              />
                            </b>
                          </Text>
                        </Table.Cell>
                      </Table.Row>

                      {/* Net movement row */}
                      <Table.Row>
                        <Table.Cell />
                        <Table.Cell />
                        <Table.Cell className="border-b-2 border-muted">
                          <Text variant={TypographyStyles.BODY_SM}>
                            <b>Net Movement</b>
                          </Text>
                        </Table.Cell>
                        <Table.Cell className="border-b-2 border-muted">
                          {((account.accountType !==
                            TransactionAccountTypes.assets &&
                            account.netTotalInCents < 0) ||
                            (account.accountType ===
                              TransactionAccountTypes.assets &&
                              account.netTotalInCents >= 0)) && (
                            <Text variant={TypographyStyles.BODY_SM}>
                              <b>
                                <CurrencyFormatLabel
                                  value={centsToDollars(
                                    Math.abs(account.netTotalInCents)
                                  )}
                                />
                              </b>
                            </Text>
                          )}
                        </Table.Cell>
                        <Table.Cell className="border-b-2 border-muted">
                          {((account.accountType !==
                            TransactionAccountTypes.assets &&
                            account.netTotalInCents >= 0) ||
                            (account.accountType ===
                              TransactionAccountTypes.assets &&
                              account.netTotalInCents < 0)) && (
                            <Text variant={TypographyStyles.BODY_SM}>
                              <b>
                                <CurrencyFormatLabel
                                  value={centsToDollars(
                                    Math.abs(account.netTotalInCents)
                                  )}
                                />
                              </b>
                            </Text>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    </Table.Body>
                  </Table>
                </div>
              </Accordion.Content>
            </Accordion.Item>
          </Accordion>
        </Card>
      </div>
    )
  }
)
